import * as React from "react";
import { Bell, BellOff, CreditCard, ShieldCheck, Smartphone, Sparkles } from "lucide-react";
import type { BasDueLeadDays, DriverProfile, NotificationSettings } from "@/types/tax";
import {
  createCheckoutSession,
  createCustomerPortalSession,
  getVapidPublicKey,
  getSubscriptionPlanPrices,
  subscribeNotifications,
  type SubscriptionPlanPrice,
  type SubscriptionPlanPrices,
  unsubscribeNotifications,
} from "@/api/client";
import { Alert } from "@/components/ui/Alert";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { Dropdown } from "@/components/ui/Dropdown";
import { Field, FieldDescription, FieldGroup, FieldLabel } from "@/components/ui/Field";
import { Input, Textarea } from "@/components/ui/Input";
import { defaultNotificationSettings } from "@/data/defaults";
import { isPaidPlan, planLabel, scanLimitForPlan, type SubscriptionCheckoutPlan } from "@/lib/plans";
import { parseMoney } from "@/lib/utils";

type PushStatus = "unsupported" | "permission_needed" | "enabled" | "blocked";

function getTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || defaultNotificationSettings.timezone;
}

function formatSubscriptionDate(value?: string | null) {
  if (!value) return "";
  const normalized = value.includes("T") ? value : value.replace(" ", "T");
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-AU", { day: "numeric", month: "short", year: "numeric" }).format(date);
}

function mergeNotificationSettings(settings?: Partial<NotificationSettings>): NotificationSettings {
  return {
    ...defaultNotificationSettings,
    ...(settings || {}),
    timezone: settings?.timezone || getTimezone(),
  };
}

function getPushStatus(pushEnabled: boolean): PushStatus {
  if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) return "unsupported";
  if (Notification.permission === "denied") return "blocked";
  if (pushEnabled && Notification.permission === "granted") return "enabled";
  return "permission_needed";
}

function urlBase64ToUint8Array(base64String: string) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = `${base64String}${padding}`.replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let index = 0; index < rawData.length; index += 1) {
    outputArray[index] = rawData.charCodeAt(index);
  }
  return outputArray;
}

async function ensureServiceWorkerRegistration() {
  const existing = await navigator.serviceWorker.getRegistration();
  if (existing) return existing;
  return navigator.serviceWorker.register("sw.js");
}

function PlanOption({
  title,
  description,
  price,
  active = false,
  badge,
  action,
}: {
  title: string;
  description: string;
  price: React.ReactNode;
  active?: boolean;
  badge?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className={active ? "rounded-lg border border-credit/30 bg-credit/5 p-3" : "rounded-lg border border-border bg-surface p-3"}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {active ? <ShieldCheck className="size-4 shrink-0 text-credit" aria-hidden="true" /> : null}
          <p className="truncate text-sm font-semibold text-ink">{title}</p>
        </div>
        {active ? <Badge variant="credit">Current</Badge> : badge ? <Badge variant="muted">{badge}</Badge> : null}
      </div>
      <p className="mt-2 text-sm leading-6 text-muted">{description}</p>
      <p className="mt-2 text-sm font-semibold tabular-nums text-ink">{price}</p>
      {action ? <div className="mt-3">{action}</div> : null}
    </div>
  );
}

function formatPlanPrice(price?: SubscriptionPlanPrice) {
  if (!price) return "Price unavailable";
  const currency = (price.currency || "AUD").toUpperCase();
  const formattedAmount = new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price.amount);
  const amount = currency === "AUD" ? `A${formattedAmount}` : formattedAmount;
  const count = price.intervalCount > 1 ? ` every ${price.intervalCount} ${price.interval}s` : ` / ${price.interval}`;
  return `${amount}${count}`;
}

export function SettingsPanel({
  profile,
  onSave,
}: {
  profile: DriverProfile;
  onSave: (profile: DriverProfile) => Promise<void> | void;
}) {
  const [draft, setDraft] = React.useState(profile);
  const [saving, setSaving] = React.useState(false);
  const [pushSaving, setPushSaving] = React.useState(false);
  const [pushMessage, setPushMessage] = React.useState("");
  const [billingBusy, setBillingBusy] = React.useState<SubscriptionCheckoutPlan | "portal" | "">("");
  const [billingMessage, setBillingMessage] = React.useState("");
  const [planPrices, setPlanPrices] = React.useState<SubscriptionPlanPrices>({});

  React.useEffect(() => setDraft({ ...profile, notificationSettings: mergeNotificationSettings(profile.notificationSettings) }), [profile]);

  React.useEffect(() => {
    let cancelled = false;
    void getSubscriptionPlanPrices().then((result) => {
      if (!cancelled && result.ok) setPlanPrices(result.data);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  function update<K extends keyof DriverProfile>(key: K, value: DriverProfile[K]) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  function updateNotifications(patch: Partial<NotificationSettings>) {
    setDraft((current) => ({
      ...current,
      notificationSettings: {
        ...mergeNotificationSettings(current.notificationSettings),
        ...patch,
        timezone: patch.timezone || current.notificationSettings?.timezone || getTimezone(),
      },
    }));
  }

  function showInstallPrompt() {
    window.dispatchEvent(new Event("rideshare-tax:show-install-prompt"));
  }

  async function startCheckout(plan: SubscriptionCheckoutPlan) {
    setBillingBusy(plan);
    setBillingMessage("");
    try {
      const result = await createCheckoutSession(plan);
      if (!result.ok || !result.data.url) {
        setBillingMessage(result.ok ? "Stripe Checkout did not return a checkout link." : result.error);
        return;
      }
      window.location.assign(result.data.url);
    } finally {
      setBillingBusy("");
    }
  }

  async function openCustomerPortal() {
    setBillingBusy("portal");
    setBillingMessage("");
    try {
      const result = await createCustomerPortalSession();
      if (!result.ok || !result.data.url) {
        setBillingMessage(result.ok ? "Stripe did not return a management link." : result.error);
        return;
      }
      window.location.assign(result.data.url);
    } finally {
      setBillingBusy("");
    }
  }

  async function enableNotifications() {
    setPushSaving(true);
    setPushMessage("");
    try {
      if (getPushStatus(false) === "unsupported") {
        setPushMessage("This browser does not support Web Push notifications. On iPhone, install RideShareTax to the Home Screen and open the installed app.");
        return;
      }

      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setPushMessage(permission === "denied" ? "Notifications are blocked in this browser. Re-enable them in browser or app settings." : "Notification permission was not granted.");
        return;
      }

      const keyResult = await getVapidPublicKey();
      if (!keyResult.ok || !keyResult.data.publicKey) {
        setPushMessage(keyResult.ok ? "Notification keys are not configured on the server." : keyResult.error);
        return;
      }

      const registration = await ensureServiceWorkerRegistration();
      const existing = await registration.pushManager.getSubscription();
      const subscription = existing || await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(keyResult.data.publicKey),
      });
      const timezone = getTimezone();
      const result = await subscribeNotifications(subscription.toJSON(), timezone);
      if (!result.ok) {
        setPushMessage(result.error);
        return;
      }

      const nextProfile = {
        ...draft,
        notificationSettings: {
          ...mergeNotificationSettings(draft.notificationSettings),
          timezone,
          pushEnabled: true,
        },
      };
      setDraft(nextProfile);
      await onSave(nextProfile);
      setPushMessage("Notifications are enabled on this device.");
    } catch (error) {
      setPushMessage(error instanceof Error ? error.message : "Could not enable notifications.");
    } finally {
      setPushSaving(false);
    }
  }

  async function disableNotifications() {
    setPushSaving(true);
    setPushMessage("");
    try {
      let endpoint: string | null = null;
      if ("serviceWorker" in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();
        const subscription = await registration?.pushManager.getSubscription();
        endpoint = subscription?.endpoint || null;
        await subscription?.unsubscribe();
      }
      const result = await unsubscribeNotifications(endpoint);
      if (!result.ok) {
        setPushMessage(result.error);
        return;
      }
      const nextProfile = {
        ...draft,
        notificationSettings: {
          ...mergeNotificationSettings(draft.notificationSettings),
          pushEnabled: false,
        },
      };
      setDraft(nextProfile);
      await onSave(nextProfile);
      setPushMessage("Notifications are disabled on this device.");
    } catch (error) {
      setPushMessage(error instanceof Error ? error.message : "Could not disable notifications.");
    } finally {
      setPushSaving(false);
    }
  }

  const notificationSettings = mergeNotificationSettings(draft.notificationSettings);
  const pushStatus = getPushStatus(notificationSettings.pushEnabled);
  const statusLabel: Record<PushStatus, string> = {
    unsupported: "Not supported",
    permission_needed: "Permission needed",
    enabled: "Enabled",
    blocked: "Blocked",
  };
  const planSource = profile.planSource || (profile.plan === "free" ? "free" : "manual");
  const isSubscribed = isPaidPlan(profile.plan);
  const renewalDate = formatSubscriptionDate(profile.subscriptionRenewsAt);
  const cancelDate = formatSubscriptionDate(profile.subscriptionCancelAt);
  const planBadge = planLabel(profile.plan);
  const planDetail = isSubscribed
    ? planSource === "manual"
      ? `Manual ${planLabel(profile.plan)} access is active on this account.`
      : cancelDate
        ? `${planLabel(profile.plan)} stays active until ${cancelDate}.`
        : renewalDate
          ? `Renews on ${renewalDate}.`
          : `Stripe ${planLabel(profile.plan)} subscription is active.`
    : "Manual income and expense entry is available on Free.";
  const quotaText = isSubscribed
    ? `${Number(profile.scanQuotaUsed || 0)} of ${Number(profile.scanQuotaLimit || scanLimitForPlan(profile.plan))} scans used this month`
    : "PDF scanning unlocks on Pro.";
  const canSubscribePro = profile.plan === "free";
  const canSubscribeProPlus = profile.plan === "free";
  const canSubscribeManualProPlus = profile.plan === "pro" && planSource === "manual";
  const showPortal = isSubscribed && Boolean(profile.billingManagementAvailable);

  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={async (event) => {
        event.preventDefault();
        setSaving(true);
        try {
          await onSave(draft);
        } finally {
          setSaving(false);
        }
      }}
    >
      <nav className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap" aria-label="Settings sections">
        {[
          ["gst-settings", "GST"],
          ["notification-settings", "Notifications"],
          ["billing-settings", "Plan"],
          ["install-settings", "Install"],
        ].map(([id, label]) => (
          <button
            key={id}
            type="button"
            className="flex min-h-11 items-center justify-center rounded-lg border border-border bg-surface px-3 text-sm font-semibold text-ink transition hover:bg-surface-strong focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            onClick={() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" })}
          >
            {label}
          </button>
        ))}
      </nav>

      <Card id="gst-settings" className="scroll-mt-24">
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle>GST settings</CardTitle>
              <CardDescription>These defaults apply to future expense entries and BAS period reminders.</CardDescription>
            </div>
            <Badge variant={isSubscribed ? "credit" : "outline"}>{planBadge}</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <FieldGroup className="grid gap-4 md:grid-cols-2">
              <Field>
                <FieldLabel htmlFor="display-name">Display name</FieldLabel>
                <Input id="display-name" value={draft.displayName} onChange={(event) => update("displayName", event.target.value)} />
              </Field>
              <Field>
                <FieldLabel htmlFor="abn">ABN</FieldLabel>
                <Input id="abn" value={draft.abn} onChange={(event) => update("abn", event.target.value)} inputMode="numeric" />
              </Field>
              <Field>
                <FieldLabel>BAS frequency</FieldLabel>
                <Dropdown
                  label="BAS frequency"
                  value={draft.basFrequency}
                  options={[
                    { value: "quarterly", label: "Quarterly" },
                    { value: "monthly", label: "Monthly" },
                  ]}
                  onChange={(value) => update("basFrequency", value)}
                />
                <FieldDescription>ATO guidance says rideshare GST cannot be annual.</FieldDescription>
              </Field>
              <Field>
                <FieldLabel htmlFor="business-use-default">Default business use</FieldLabel>
                <Input
                  id="business-use-default"
                  inputMode="numeric"
                  value={draft.defaultBusinessUsePercent}
                  onChange={(event) => update("defaultBusinessUsePercent", parseMoney(event.target.value))}
                />
              </Field>
              <Field className="md:col-span-2">
                <FieldLabel htmlFor="vehicle-notes">Vehicle notes</FieldLabel>
                <Textarea id="vehicle-notes" value={draft.vehicleNotes} onChange={(event) => update("vehicleNotes", event.target.value)} />
              </Field>
          </FieldGroup>
        </CardContent>
      </Card>

          <section id="billing-settings" className="order-3 scroll-mt-24 rounded-lg border border-border bg-white p-4" aria-labelledby="billing-settings-title">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="flex min-w-0 gap-3">
                <div className="flex size-11 shrink-0 items-center justify-center rounded-lg border border-primary/20 bg-primary/5 text-primary">
                  <Sparkles className="size-5" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 id="billing-settings-title" className="font-semibold text-ink">Subscription</h3>
                    <Badge variant={isSubscribed ? "credit" : "outline"}>{planBadge}</Badge>
                    {profile.subscriptionStatus ? <Badge variant="muted">{profile.subscriptionStatus}</Badge> : null}
                  </div>
                  <p className="mt-1 text-sm leading-6 text-muted">{planDetail}</p>
                  <p className="mt-1 text-xs font-semibold text-muted">{quotaText}</p>
                </div>
              </div>

              {showPortal ? (
                <Button type="button" variant="secondary" className="w-full lg:w-auto" disabled={billingBusy !== ""} onClick={openCustomerPortal}>
                  <CreditCard className="size-4" aria-hidden="true" />
                  {billingBusy === "portal" ? "Opening..." : "Manage subscription"}
                </Button>
              ) : canSubscribeManualProPlus ? (
                <Button type="button" variant="credit" className="w-full lg:w-auto" disabled={billingBusy !== ""} onClick={() => startCheckout("pro_plus")}>
                  <CreditCard className="size-4" aria-hidden="true" />
                  {billingBusy === "pro_plus" ? "Opening..." : "Subscribe to Pro Plus"}
                </Button>
              ) : null}
            </div>

            <div className="mt-4 grid gap-3 lg:grid-cols-3">
              <PlanOption
                title="Free"
                description="Manually enter monthly income and business expenses. GST worksheets update from saved records."
                price="A$0.00 / month"
                badge={profile.plan === "free" ? "Current" : undefined}
              />
              <PlanOption
                title="Pro"
                description="Scan up to 50 income summaries or expense bills each month, then review before saving."
                price={formatPlanPrice(planPrices.pro)}
                active={profile.plan === "pro"}
                action={
                  canSubscribePro ? (
                    <Button type="button" variant="secondary" className="w-full" disabled={billingBusy !== ""} onClick={() => startCheckout("pro")}>
                      <CreditCard className="size-4" aria-hidden="true" />
                      {billingBusy === "pro" ? "Opening..." : "Subscribe to Pro"}
                    </Button>
                  ) : undefined
                }
              />
              <PlanOption
                title="Pro Plus"
                description="Scan up to 150 income summaries or expense bills each month for heavier document workflows."
                price={formatPlanPrice(planPrices.pro_plus)}
                active={profile.plan === "pro_plus"}
                action={
                  canSubscribeProPlus ? (
                    <Button type="button" variant="credit" className="w-full" disabled={billingBusy !== ""} onClick={() => startCheckout("pro_plus")}>
                      <CreditCard className="size-4" aria-hidden="true" />
                      {billingBusy === "pro_plus" ? "Opening..." : "Subscribe to Pro Plus"}
                    </Button>
                  ) : profile.plan === "pro" && showPortal ? (
                    <Button type="button" variant="secondary" className="w-full" disabled={billingBusy !== ""} onClick={openCustomerPortal}>
                      <CreditCard className="size-4" aria-hidden="true" />
                      {billingBusy === "portal" ? "Opening..." : "Manage upgrade"}
                    </Button>
                  ) : undefined
                }
              />
            </div>

            {billingMessage ? (
              <Alert className="mt-3" variant="danger" title="Subscription">
                {billingMessage}
              </Alert>
            ) : null}
          </section>

          <section id="notification-settings" className="order-2 scroll-mt-24 rounded-lg border border-border bg-white p-4" aria-labelledby="notification-settings-title">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h3 id="notification-settings-title" className="font-semibold text-ink">Notifications</h3>
                <p className="mt-1 text-sm leading-6 text-muted">Get reminders to upload records and review BAS due dates.</p>
              </div>
              <Badge variant={pushStatus === "enabled" ? "credit" : pushStatus === "blocked" ? "danger" : "outline"}>{statusLabel[pushStatus]}</Badge>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <label className="flex min-h-16 items-start gap-3 rounded-lg border border-border bg-surface p-3">
                <input
                  type="checkbox"
                  className="mt-1 size-5 accent-primary"
                  checked={notificationSettings.monthlyUploadEnabled}
                  onChange={(event) => updateNotifications({ monthlyUploadEnabled: event.target.checked })}
                />
                <span>
                  <span className="block text-sm font-semibold text-ink">Monthly upload reminder</span>
                  <span className="mt-1 block text-xs leading-5 text-muted">On the 1st, remind me to review or upload last month&apos;s income and expense records.</span>
                </span>
              </label>

              <label className="flex min-h-16 items-start gap-3 rounded-lg border border-border bg-surface p-3">
                <input
                  type="checkbox"
                  className="mt-1 size-5 accent-primary"
                  checked={notificationSettings.basDueEnabled}
                  onChange={(event) => updateNotifications({ basDueEnabled: event.target.checked })}
                />
                <span>
                  <span className="block text-sm font-semibold text-ink">BAS due reminder</span>
                  <span className="mt-1 block text-xs leading-5 text-muted">Remind me before each quarterly BAS due date based on the lead time below.</span>
                </span>
              </label>

              <Field>
                <FieldLabel>BAS due lead time</FieldLabel>
                <Dropdown
                  label="BAS due lead time"
                  value={String(notificationSettings.basDueLeadDays)}
                  options={[
                    { value: "3", label: "3 days before" },
                    { value: "7", label: "7 days before" },
                    { value: "14", label: "14 days before" },
                  ]}
                  onChange={(value) => updateNotifications({ basDueLeadDays: Number(value) as BasDueLeadDays })}
                />
              </Field>

              <div className="flex min-w-0 flex-col gap-2">
                <p className="text-sm font-semibold text-ink">Timezone</p>
                <div className="flex min-h-11 items-center rounded-lg border border-border bg-surface px-3 py-2">
                  <p className="break-words text-sm font-semibold text-ink">{notificationSettings.timezone}</p>
                </div>
                <p className="text-xs leading-5 text-muted">Detected from this device. Used for reminder timing.</p>
              </div>
            </div>

            <p className="mt-4 rounded-lg border border-primary/15 bg-primary/5 px-3 py-2 text-xs leading-5 text-muted">
              iPhone and iPad notifications require RideShareTax to be installed from Safari as a Home Screen app. If permission is blocked, re-enable it in browser or app settings.
            </p>

            {pushMessage ? (
              <Alert className="mt-3" variant={pushStatus === "enabled" ? "success" : pushStatus === "blocked" ? "danger" : "info"} title="Notification status">
                {pushMessage}
              </Alert>
            ) : null}

            <div className="mt-4 flex flex-col gap-3 sm:flex-row">
              {pushStatus === "enabled" ? (
                <Button type="button" variant="secondary" disabled={pushSaving} onClick={disableNotifications}>
                  <BellOff className="size-4" aria-hidden="true" />
                  {pushSaving ? "Disabling..." : "Disable notifications"}
                </Button>
              ) : (
                <Button type="button" disabled={pushSaving || pushStatus === "blocked" || pushStatus === "unsupported"} onClick={enableNotifications}>
                  <Bell className="size-4" aria-hidden="true" />
                  {pushSaving ? "Enabling..." : "Enable notifications"}
                </Button>
              )}
            </div>
          </section>

          <section id="install-settings" className="order-4 scroll-mt-24 rounded-lg border border-border bg-white p-4" aria-labelledby="install-settings-title">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 gap-3">
                <div className="flex size-11 shrink-0 items-center justify-center rounded-lg border border-border bg-surface text-primary">
                  <Smartphone className="size-5" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <h3 id="install-settings-title" className="font-semibold text-ink">Install on this device</h3>
                  <p className="mt-1 text-sm leading-6 text-muted">Use the home screen version for quicker mobile access.</p>
                </div>
              </div>
              <Button type="button" variant="secondary" className="w-full sm:w-auto" onClick={showInstallPrompt}>
                Add to Home Screen
              </Button>
            </div>
          </section>

          <div className="order-5 sticky bottom-[calc(4.125rem+env(safe-area-inset-bottom))] z-20 -mx-4 border-t border-border bg-white/95 px-4 py-3 backdrop-blur sm:static sm:mx-0 sm:border-0 sm:bg-transparent sm:p-0 sm:backdrop-blur-0">
            <Button type="submit" disabled={saving} className="w-full sm:w-auto">
              {saving ? "Saving settings..." : "Save settings"}
            </Button>
          </div>
    </form>
  );
}
